import { useState, useEffect, Fragment, useMemo } from "react";
import { useLocation, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Edit, Trash2, Trash, Filter, CheckCircle, Upload, Search, Users, Trophy, CreditCard, Download, Calendar, DollarSign, FileCheck, FileText, Tag, BookOpen, BarChart3, TrendingUp, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, UserPlus, Copy, Check, XCircle, AlertCircle, Loader2, Activity, ArrowUpDown, ArrowUp, ArrowDown, Target, Maximize2, Minimize2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ImageUpload } from "@/components/ImageUpload";
import DOMPurify from 'dompurify';
import { getDisplayName } from "@/lib/userDisplay";
import AdminPrizeCampaigns from "./AdminPrizeCampaigns";
import AdminActivityStream from "./AdminActivityStream";
import type { User } from "@shared/schema";

interface QuestionOption {
  id: string;
  text: string;
  imageUrl?: string;
  math?: string;
  // Table grid specific properties
  rows?: number;
  cols?: number;
  cells?: string[];
  correctCells?: number[];
  numRowHeaders?: number;
  numColHeaders?: number;
  rowHeaders?: string[];
  colHeaders?: string[];
}

interface QuestionObjectiveTag {
  code: string;
  description: string;
}

interface Question {
  id: string;
  grade: number;
  subject: string;
  topic: string;
  questionType: string;
  questionText: string;
  questionImageUrl?: string;
  questionMath?: string;
  options: Array<string | QuestionOption>;
  correctAnswer: string;
  explanation: string;
  explanationImageUrl?: string;
  explanationMath?: string;
  aiExplanation?: string;
  difficulty: string;
  points: number;
  managedDifficulty: boolean;
  tags: string[];
  createdBy: string;
  generationMethod: string;
  status: string;
  reviewNotes?: string;
  isActive: boolean;
  hint?: string;
  quizzativeId?: string;
  createdAt?: string;
  updatedAt?: string;
  objectives?: QuestionObjectiveTag[];
}

interface PaginatedResponse {
  questions: Question[];
  pagination: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
  };
}

interface QuestionFormData {
  grade: number;
  subject: string;
  topic: string;
  questionType: string;
  questionText: string;
  questionImageUrl?: string;
  questionMath?: string;
  options: Array<{ text: string; imageUrl?: string; math?: string }>;
  correctAnswer: string;
  explanation: string;
  explanationImageUrl?: string;
  explanationMath?: string;
  difficulty: string;
  tags: string[];
  status: string;
  hint?: string;
}

const defaultFormData: QuestionFormData = {
  grade: 6,
  subject: "",
  topic: "",
  questionType: "multiple-choice",
  questionText: "",
  options: [
    { text: "" },
    { text: "" },
    { text: "" },
    { text: "" }
  ],
  correctAnswer: "0",
  explanation: "",
  difficulty: "neutral",
  tags: [],
  status: "sandbox",
  hint: ""
};

function QuestionList({ 
  filters, 
  onEdit,
}: { 
  filters: any, 
  onEdit: (q: Question) => void,
}) {
  const { toast } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  useEffect(() => { setCurrentPage(1); }, [filters, perPage]);

  const { data, isLoading, refetch } = useQuery<PaginatedResponse>({
    queryKey: ["/api/admin/questions", filters, currentPage, perPage, sortBy, sortOrder],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== "all") params.append(key, String(value));
      });
      params.set("page", String(currentPage));
      params.set("per_page", String(perPage));
      params.set("sort_by", sortBy);
      params.set("sort_order", sortOrder);
      const res = await fetch(`/api/admin/questions?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch questions");
      return res.json();
    },
  });

  const questions = data?.questions || [];
  const pagination = data?.pagination || { page: 1, perPage: 25, total: 0, totalPages: 0 };

  const [deleteQuestionId, setDeleteQuestionId] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/questions/${id}`);
    },
    onSuccess: () => {
      toast({ title: "Question deleted successfully" });
      setDeleteQuestionId(null);
      refetch();
    }
  });

  const publishMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string, status: string }) => {
      await apiRequest("PATCH", `/api/admin/questions/${id}/status`, { status });
    },
    onSuccess: () => {
      toast({ title: "Question status updated" });
      refetch();
    }
  });

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-30" />;
    return sortOrder === "asc" 
      ? <ArrowUp className="h-3 w-3 ml-1" /> 
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const statusColor = (status: string) => {
    if (status === "published") return "default" as const;
    if (status === "ready") return "secondary" as const;
    return "outline" as const;
  };

  const difficultyColor = (diff: string) => {
    if (diff === "hard") return "text-red-600 dark:text-red-400 border-red-300 dark:border-red-700";
    if (diff === "medium") return "text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700";
    return "text-green-600 dark:text-green-400 border-green-300 dark:border-green-700";
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading questions...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">
          Showing {questions.length > 0 ? ((currentPage - 1) * perPage + 1) : 0}–{Math.min(currentPage * perPage, pagination.total)} of {pagination.total} questions
        </p>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Per page:</span>
          <Select value={String(perPage)} onValueChange={(v) => setPerPage(Number(v))}>
            <SelectTrigger className="w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[35%]">
                <button className="flex items-center font-medium" onClick={() => handleSort("created_at")}>
                  Question <SortIcon field="created_at" />
                </button>
              </TableHead>
              <TableHead>
                <button className="flex items-center font-medium" onClick={() => handleSort("subject")}>
                  Subject <SortIcon field="subject" />
                </button>
              </TableHead>
              <TableHead>
                <button className="flex items-center font-medium" onClick={() => handleSort("topic")}>
                  Topic <SortIcon field="topic" />
                </button>
              </TableHead>
              <TableHead>
                <button className="flex items-center font-medium" onClick={() => handleSort("grade")}>
                  Grade <SortIcon field="grade" />
                </button>
              </TableHead>
              <TableHead>
                <button className="flex items-center font-medium" onClick={() => handleSort("difficulty")}>
                  Diff. <SortIcon field="difficulty" />
                </button>
              </TableHead>
              <TableHead>
                <button className="flex items-center font-medium" onClick={() => handleSort("status")}>
                  Status <SortIcon field="status" />
                </button>
              </TableHead>
              <TableHead>Objectives</TableHead>
              <TableHead>Source</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {questions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  No questions found matching your filters.
                </TableCell>
              </TableRow>
            ) : questions.map((question) => (
              <TableRow key={question.id}>
                <TableCell className="max-w-[350px]">
                  <div 
                    className="line-clamp-2 text-sm"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.questionText) }}
                  />
                  {question.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {question.tags.slice(0, 3).map(tag => (
                        <Badge key={tag} variant="secondary" className="text-[10px]">
                          {tag}
                        </Badge>
                      ))}
                      {question.tags.length > 3 && (
                        <span className="text-[10px] text-muted-foreground">+{question.tags.length - 3}</span>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell className="capitalize text-xs whitespace-nowrap">{question.subject}</TableCell>
                <TableCell className="capitalize text-xs">{question.topic.replace(/-/g, ' ')}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">G{question.grade}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`capitalize text-[10px] ${difficultyColor(question.difficulty)}`}>
                    {question.difficulty}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={statusColor(question.status)} className="capitalize text-[10px]">
                    {question.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {question.objectives && question.objectives.length > 0 ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1 cursor-default">
                          <Target className="h-3 w-3 text-green-600 dark:text-green-400" />
                          <span className="text-xs text-green-600 dark:text-green-400 font-medium">{question.objectives.length}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-xs">
                        <div className="space-y-1">
                          {question.objectives.map((obj, i) => (
                            <div key={i} className="text-xs">
                              <span className="font-medium">{obj.code}</span>: {obj.description}
                            </div>
                          ))}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="text-xs text-muted-foreground">--</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-[10px]">
                    {question.quizzativeId ? "QZ" : question.generationMethod === "ai" ? "AI" : "Manual"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {question.status !== 'published' && (
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => publishMutation.mutate({ id: question.id, status: 'published' })}
                        disabled={publishMutation.isPending}
                      >
                        Publish
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => onEdit(question)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="icon" 
                      variant="ghost"
                      className="text-destructive"
                      onClick={() => {
                        setDeleteQuestionId(question.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.totalPages}
          </p>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="outline" disabled={currentPage <= 1} onClick={() => setCurrentPage(1)}>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="outline" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              let pageNum: number;
              if (pagination.totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= pagination.totalPages - 2) {
                pageNum = pagination.totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <Button
                  key={pageNum}
                  size="icon"
                  variant={pageNum === currentPage ? "default" : "outline"}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
            <Button size="icon" variant="outline" disabled={currentPage >= pagination.totalPages} onClick={() => setCurrentPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="outline" disabled={currentPage >= pagination.totalPages} onClick={() => setCurrentPage(pagination.totalPages)}>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteQuestionId} onOpenChange={(open) => { if (!open) setDeleteQuestionId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Question</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this question. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteQuestionId) deleteMutation.mutate(deleteQuestionId); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AdminSection({ section }: { section: string }) {
  const [location, setLocation] = useLocation();
  
  // Load admin working grade from localStorage or default to 6
  const [adminGrade, setAdminGrade] = useState<number>(() => {
    if (typeof window === 'undefined') return 6; // SSR safety
    const saved = localStorage.getItem('adminWorkingGrade');
    return saved ? parseInt(saved) : 6;
  });
  
  const [filters, setFilters] = useState(() => ({ 
    grade: adminGrade.toString(), 
    subject: "all", 
    topic: "all", 
    difficulty: "all",
    status: "!sandbox",
    tagged: "all",
    search: "",
    generation_method: "all",
  }));
  const [searchInput, setSearchInput] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullscreen) setIsFullscreen(false);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isFullscreen]);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  
  // Initialize formData with adminGrade
  const [formData, setFormData] = useState<QuestionFormData>(() => ({
    ...defaultFormData,
    grade: adminGrade
  }));
  
  // Initialize aiGenerationData with adminGrade
  const [aiGenerationData, setAiGenerationData] = useState(() => ({
    grade: adminGrade,
    subject: "", // Will be selected from dropdown
    topic: "", // Will be selected from dropdown based on available topics
    difficulty: "medium",
    count: 1
  }));
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const handleGradeChange = (gradeStr: string) => {
    if (gradeStr === "all") {
      setFilters(prev => ({ ...prev, grade: "all", subject: "all", topic: "all" }));
    } else {
      const grade = parseInt(gradeStr);
      setAdminGrade(grade);
      if (typeof window !== 'undefined') {
        localStorage.setItem('adminWorkingGrade', grade.toString());
      }
      setFilters(prev => ({ ...prev, grade: grade.toString(), subject: "all", topic: "all" }));
      setFormData(prev => ({ ...prev, grade }));
      setAiGenerationData(prev => ({ ...prev, grade }));
    }
  };

  // Fetch grades and subjects from API
  const { data: grades = [] } = useQuery<Array<{id: string; gradeNumber: number; displayName: string; isActive: boolean; sortOrder: number}>>({
    queryKey: ['/api/admin/grades'],
  });

  const { data: subjects = [] } = useQuery<Array<{id: string; gradeId: string; name: string; displayName: string; isActive: boolean; sortOrder: number}>>({
    queryKey: ['/api/admin/subjects'],
  });

  const { data: topics = [] } = useQuery<Array<{id: string; subjectId: string; name: string; displayName: string; isActive: boolean; sortOrder: number}>>({
    queryKey: ['/api/admin/topics'],
  });

  if (section === "questions") {
    const questionsContent = (
      <div className={`space-y-6 ${isFullscreen ? "fixed inset-0 z-50 bg-background p-6 overflow-auto" : ""}`}>
        <div className="flex justify-between items-center flex-wrap gap-4">
          <h1 className="text-3xl font-bold tracking-tight">Manage Questions</h1>
          <div className="flex gap-2">
            <Button onClick={() => setIsCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Question
            </Button>
            <Button variant="outline" onClick={() => setLocation("/admin/ai-import")}>
              <Upload className="w-4 h-4 mr-2" />
              AI Import
            </Button>
            <Button variant="outline" size="icon" onClick={() => setIsFullscreen(!isFullscreen)}>
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-medium flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Grade</Label>
                <Select 
                  value={filters.grade} 
                  onValueChange={(v) => handleGradeChange(v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Grade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Grades</SelectItem>
                    <SelectItem value="4">Grade 4</SelectItem>
                    <SelectItem value="5">Grade 5</SelectItem>
                    <SelectItem value="6">Grade 6</SelectItem>
                    <SelectItem value="11">Grade 11</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Subject</Label>
                <Select 
                  value={filters.subject} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, subject: v, topic: "all" }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Subject" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Subjects</SelectItem>
                    {(filters.grade === "all" 
                      ? Array.from(new Map(subjects.map(s => [s.name, s])).values())
                      : subjects.filter(s => s.gradeId === grades.find(g => g.gradeNumber === parseInt(filters.grade))?.id)
                    ).map(s => (
                      <SelectItem key={s.id} value={s.name}>{s.displayName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Topic</Label>
                <Select 
                  value={filters.topic} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, topic: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Topic" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Topics</SelectItem>
                    {topics.filter(t => t.subjectId === subjects.find(s => s.name === filters.subject)?.id).map(t => (
                      <SelectItem key={t.id} value={t.name}>{t.displayName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Difficulty</Label>
                <Select 
                  value={filters.difficulty} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, difficulty: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Difficulty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select 
                  value={filters.status} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="!sandbox">All (excl. sandbox)</SelectItem>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="ready">Ready</SelectItem>
                    <SelectItem value="sandbox">Sandbox</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Objectives</Label>
                <Select 
                  value={filters.tagged} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, tagged: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tagged" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="tagged">Tagged</SelectItem>
                    <SelectItem value="untagged">Untagged</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Source</Label>
                <Select 
                  value={filters.generation_method} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, generation_method: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="ai">AI Generated</SelectItem>
                    <SelectItem value="import">Imported</SelectItem>
                    <SelectItem value="quizzative">Quizzative</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search..." 
                    className="pl-8"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <QuestionList 
          filters={filters} 
          onEdit={(q) => {
            setEditingQuestion(q);
          }} 
        />

      </div>
    );
    return questionsContent;
  }

  if (section === "question-analytics") {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">Question Analytics</h1>
        <p className="text-muted-foreground">Detailed performance analysis of questions across the platform.</p>
        <Card className="p-12 text-center text-muted-foreground border-dashed">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-20" />
          Analytics dashboard coming soon.
        </Card>
      </div>
    );
  }

  if (section === "cancellation-feedback") {
    return <CancellationFeedbackSection />;
  }

  if (section === "activity") {
    return <AdminActivityStream />;
  }

  if (section === "campaigns") {
    return <AdminPrizeCampaigns />;
  }

  if (section === "dummy-accounts") {
    return <DummyAccountGenerator />;
  }

  return (
    <div className="p-12 text-center text-muted-foreground">
      Select a valid section to view content.
    </div>
  );
}

interface CancellationEntry {
  id: string;
  reason: string;
  otherDetails: string | null;
  subscriptionStatus: string | null;
  subscriptionPlan: string | null;
  createdAt: string;
  userId: string;
  userEmail: string | null;
  userFirstName: string | null;
  userLastName: string | null;
}

interface CancellationFeedbackData {
  total: number;
  breakdown: { reason: string; count: number }[];
  entries: CancellationEntry[];
}

function CancellationFeedbackSection() {
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery<CancellationFeedbackData>({
    queryKey: ["/api/admin/cancellation-feedback"],
  });

  const filtered = useMemo(() => {
    if (!data?.entries) return [];
    if (!search.trim()) return data.entries;
    const q = search.toLowerCase();
    return data.entries.filter(
      (e) =>
        e.reason.toLowerCase().includes(q) ||
        (e.userEmail ?? "").toLowerCase().includes(q) ||
        (e.userFirstName ?? "").toLowerCase().includes(q) ||
        (e.userLastName ?? "").toLowerCase().includes(q) ||
        (e.otherDetails ?? "").toLowerCase().includes(q)
    );
  }, [data, search]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Cancellation Feedback</h1>
        <p className="text-muted-foreground mt-1">Why subscribers are leaving, collected at the point of cancellation.</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Loading feedback...
        </div>
      ) : !data || data.total === 0 ? (
        <Card className="p-12 text-center text-muted-foreground border-dashed">
          <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-20" />
          No cancellation feedback yet.
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total Cancellations</CardDescription>
                <CardTitle className="text-4xl">{data.total}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Top Reason</CardDescription>
                <CardTitle className="text-lg leading-snug">{data.breakdown[0]?.reason ?? "–"}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{data.breakdown[0]?.count} people selected this</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Reason Breakdown</CardTitle>
              <CardDescription>How many people selected each reason</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.breakdown.map(({ reason, count }) => {
                const pct = Math.round((count / data.total) * 100);
                return (
                  <div key={reason}>
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <span className="text-sm flex-1">{reason}</span>
                      <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">{count} ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
              <div>
                <CardTitle className="text-base">Individual Responses</CardTitle>
                <CardDescription>{data.total} total</CardDescription>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email or reason…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No results match your search.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          <div className="text-sm font-medium">
                            {entry.userFirstName || entry.userLastName
                              ? `${entry.userFirstName ?? ""} ${entry.userLastName ?? ""}`.trim()
                              : "Unknown"}
                          </div>
                          <div className="text-xs text-muted-foreground">{entry.userEmail ?? entry.userId}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{entry.reason}</div>
                          {entry.otherDetails && (
                            <div className="text-xs text-muted-foreground mt-0.5 max-w-xs truncate" title={entry.otherDetails}>
                              {entry.otherDetails}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {entry.subscriptionPlan ? (
                            <Badge variant="secondary" className="capitalize text-xs">
                              {entry.subscriptionPlan}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">–</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {new Date(entry.createdAt).toLocaleDateString("en-JM", {
                            timeZone: "America/Jamaica",
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

    </div>
  );
}

interface DummyAccount {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  grade: number;
}

function DummyAccountGenerator() {
  const { toast } = useToast();
  const [count, setCount] = useState(1);
  const [grade, setGrade] = useState("6");
  const [plan, setPlan] = useState("monthly");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { data: existingDummyAccounts = [], refetch: refetchDummies } = useQuery<User[]>({
    queryKey: ["/api/admin/dummy-accounts"],
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/generate-dummy-accounts", {
        count,
        grade: parseInt(grade),
        plan,
      });
      return res.json();
    },
    onSuccess: (data: { created: number; accounts: DummyAccount[]; plan: string; subscriptionEnd: string }) => {
      refetchDummies();
      toast({
        title: `${data.created} account${data.created !== 1 ? "s" : ""} created`,
        description: `All accounts have an active ${data.plan} plan.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
    },
    onError: () => {
      toast({
        title: "Failed to generate accounts",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("DELETE", `/api/admin/users/${userId}`);
    },
    onSuccess: () => {
      refetchDummies();
      toast({ title: "Dummy account deleted" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
    },
  });

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Generate Dummy Test Accounts
          </CardTitle>
          <CardDescription>
            Create test accounts with active subscriptions for platform testing. All accounts use the same password (TestPass123!) and have @edufocal-test.com email addresses. Accounts are automatically deleted after 5 days.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="space-y-2">
              <Label htmlFor="dummy-count">Number of Accounts</Label>
              <Input
                id="dummy-count"
                type="number"
                min={1}
                max={50}
                value={count}
                onChange={(e) => setCount(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))}
              />
              <p className="text-xs text-muted-foreground">Max 50 at a time</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dummy-grade">Grade</Label>
              <Select value={grade} onValueChange={setGrade}>
                <SelectTrigger id="dummy-grade">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">Grade 4</SelectItem>
                  <SelectItem value="5">Grade 5</SelectItem>
                  <SelectItem value="6">Grade 6</SelectItem>
                  <SelectItem value="11">Grade 11</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dummy-plan">Subscription Plan</Label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger id="dummy-plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button 
            className="w-full" 
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
          >
            {generateMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <UserPlus className="w-4 h-4 mr-2" />
            )}
            Generate Accounts
          </Button>
        </CardContent>
      </Card>

      {existingDummyAccounts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Active Dummy Accounts</CardTitle>
            <CardDescription>Accounts created for testing that are currently active in the system.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-5 font-medium text-sm text-muted-foreground px-2">
                <div>Name</div>
                <div>Email</div>
                <div>Grade</div>
                <div>Plan</div>
                <div className="text-right">Actions</div>
              </div>
              {existingDummyAccounts.map((account) => (
                <div key={account.id} className="grid grid-cols-5 items-center bg-muted/50 p-2 rounded-lg text-sm">
                  <div>{account.firstName} {account.lastName}</div>
                  <div className="font-mono text-xs truncate mr-2">{account.email}</div>
                  <div>Grade {account.grade}</div>
                  <div className="capitalize">{account.subscriptionPlan}</div>
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => copyToClipboard(`${account.email}\tTestPass123!`, account.id)}
                      title="Copy email and password"
                    >
                      {copiedId === account.id ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => deleteMutation.mutate(account.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function AdminSectionWithGrade({ section }: { section: string }) {
  return <AdminSection section={section} />;
}
